import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  MagnifyingGlass, ArrowRight, CheckCircle, Star, ShieldCheck, Lightning,
  IdentificationCard, Fingerprint, AirplaneTilt, Receipt, Factory, Storefront,
  HouseSimple, Buildings, Bank, ForkKnife, IdentificationBadge, Car,
  Lightning as Bolt, DeviceMobile, Television, Umbrella, WhatsappLogo, Sparkle,
  ChatCircleDots, MapPin, Users, Handshake, Phone, TrendUp, Robot, FileText, Wallet,
} from "@phosphor-icons/react";
import { BRAND, waLink } from "@/lib/brand";

// icon lookup used by the public services array
const ICONS = {
  IdentificationCard, Fingerprint, AirplaneTilt, Receipt, Factory, Storefront,
  HouseSimple, Buildings, Bank, ForkKnife, IdentificationBadge, Car, Lightning: Bolt,
  DeviceMobile, Television, Umbrella,
};

export default function PublicHome() {
  const nav = useNavigate();
  const [popular, setPopular] = useState([]);
  const [stats, setStats] = useState({ customers: 0, applications: 0, branches: 0, since_year: 2015 });
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/public/site").then((r) => setPopular(r.data.popular_services || [])).catch(() => {});
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return popular;
    const q = query.toLowerCase();
    return popular.filter((s) => s.name.toLowerCase().includes(q) || s.category.toLowerCase().includes(q) || s.hint.toLowerCase().includes(q));
  }, [popular, query]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) { nav("/services"); return; }
    nav(`/services?q=${encodeURIComponent(query.trim())}`);
  };

  return (
    <div data-testid="public-home">
      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1e3a8a] via-[#1e40af] to-[#3b82f6] text-white">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2260%22 height=%2260%22><path d=%22M 0 30 L 60 30 M 30 0 L 30 60%22 stroke=%22%23fff%22 stroke-opacity=%220.06%22 stroke-width=%221%22/></svg>')",
        }} />
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-orange-500/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-xs font-medium mb-6" data-testid="hero-badge">
              <Sparkle size={12} weight="fill" className="text-orange-300" /> India's Complete Digital Service Platform
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-5" style={{ fontFamily: "var(--font-heading)" }}>
              Every government service.<br />
              <span className="text-orange-300">One trusted platform.</span>
            </h1>
            <p className="text-base sm:text-lg text-blue-100/90 max-w-2xl mb-8 leading-relaxed">
              PAN · Aadhaar · Passport · GST · UDYAM · Rent Agreements &amp; 100+ services — apply online, track your application, and get it done at your nearest branch. All powered by DigitalSetu.
            </p>

            {/* Big search bar */}
            <form onSubmit={submitSearch} className="max-w-2xl bg-white rounded-full flex items-center pl-5 pr-2 py-2 shadow-2xl shadow-blue-950/30" data-testid="hero-search-form">
              <MagnifyingGlass size={20} className="text-slate-400 shrink-0" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search a service — PAN card, Aadhaar update, GST…"
                className="flex-1 px-3 py-2 text-slate-900 outline-none bg-transparent"
                data-testid="hero-search-input"
              />
              <button type="submit" className="inline-flex items-center gap-1.5 px-5 h-11 rounded-full bg-[#ea580c] hover:bg-orange-700 text-white text-sm font-semibold transition-colors" data-testid="hero-search-btn">
                Search <ArrowRight size={14} />
              </button>
            </form>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/apply" className="inline-flex items-center gap-1.5 px-5 h-11 rounded-full bg-white text-[#1e3a8a] text-sm font-semibold hover:-translate-y-0.5 transition-transform" data-testid="hero-cta-apply">
                Apply Online <ArrowRight size={14} />
              </Link>
              <Link to="/track" className="inline-flex items-center gap-1.5 px-5 h-11 rounded-full bg-white/10 backdrop-blur border border-white/25 text-white text-sm font-semibold hover:bg-white/20" data-testid="hero-cta-track">
                Track My Application
              </Link>
              <a href={waLink("Hi Priyanka Enterprises, I want to apply for a service.")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-5 h-11 rounded-full bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600" data-testid="hero-cta-whatsapp">
                <WhatsappLogo size={14} weight="fill" /> WhatsApp
              </a>
            </div>

            {/* Trust bar */}
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-blue-100">
              <div className="flex items-center gap-2"><ShieldCheck size={16} weight="fill" className="text-emerald-300" /> AES-256 secured</div>
              <div className="flex items-center gap-2"><Lightning size={16} weight="fill" className="text-orange-300" /> Same-day processing</div>
              <div className="flex items-center gap-2"><Star size={16} weight="fill" className="text-yellow-300" /> Since {stats.since_year}</div>
              <div className="flex items-center gap-2"><Users size={16} weight="fill" className="text-emerald-300" /> {stats.customers.toLocaleString()}+ customers served</div>
            </div>
          </div>

          {/* Right — Stats card grid */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Services" value={16} suffix="+" icon={Receipt} tone="bg-white/10 border-white/20" data-testid="stat-services" />
              <StatCard label="Branches" value={stats.branches || 4} suffix="+" icon={Buildings} tone="bg-white/10 border-white/20" data-testid="stat-branches" />
              <StatCard label="Applications" value={stats.applications} suffix="+" icon={FileText} tone="bg-white/10 border-white/20" data-testid="stat-applications" />
              <StatCard label="Customers" value={stats.customers} suffix="+" icon={Users} tone="bg-white/10 border-white/20" data-testid="stat-customers" />
            </div>
            <div className="mt-4 rounded-2xl bg-white/10 border border-white/20 backdrop-blur p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkle size={16} weight="fill" className="text-orange-300" />
                <div className="text-sm font-semibold">AI Document Assistant</div>
              </div>
              <p className="text-xs text-blue-100/90 leading-relaxed">Ask us any question about a service — our AI Interview Mode drafts your rent agreement, complaint or affidavit in minutes.</p>
              <Link to="/services" className="inline-flex items-center gap-1 text-xs text-orange-300 font-semibold mt-2">Explore services <ArrowRight size={12} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* ================= POPULAR SERVICES ================= */}
      <section className="py-20 bg-slate-50" data-testid="popular-services-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#1e3a8a] mb-1.5">Popular Services</div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>What can we help you with today?</h2>
            </div>
            <Link to="/services" className="text-sm font-semibold text-[#1e3a8a] hover:text-orange-600 inline-flex items-center gap-1">View all services <ArrowRight size={12} /></Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {(filtered.length ? filtered : popular).slice(0, 16).map((s) => {
              const Ic = ICONS[s.icon] || IdentificationCard;
              return (
                <Link
                  key={s.slug}
                  to={`/apply?service=${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group relative bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#1e3a8a] hover:shadow-lg hover:-translate-y-1 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-3 group-hover:bg-[#1e3a8a] transition-colors">
                    <Ic size={22} className="text-[#1e3a8a] group-hover:text-white transition-colors" weight="duotone" />
                  </div>
                  <div className="font-semibold text-slate-900 leading-tight mb-1">{s.name}</div>
                  <div className="text-[11px] text-slate-500 leading-snug">{s.hint}</div>
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowRight size={14} className="text-[#1e3a8a]" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= BUSINESS FEATURES ================= */}
      <section className="py-20 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#1e3a8a] mb-1.5">Complete Business Platform</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: "var(--font-heading)" }}>Not just services — your complete business OS.</h2>
            <p className="text-slate-600">Everything a modern Common Service Centre needs, in one clean, secure workspace.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Receipt,      title: "Online Billing & POS",       desc: "GST-ready invoices, POS billing, printable receipts, and multi-payment reconciliation." },
              { icon: Wallet,       title: "Payment Collection",         desc: "UPI, cards, wallets, cash and Razorpay/PhonePe/Paytm — track every rupee." },
              { icon: Handshake,    title: "CRM & Lead Management",      desc: "Meta / Website / Manual leads. Analytics, funnel, and one-click conversion to customer." },
              { icon: Robot,        title: "AI Draft Generator",         desc: "Interview-mode drafting for Rent Agreement, Affidavit, Complaints — powered by Claude Sonnet." },
              { icon: FileText,     title: "Document Management",        desc: "AES-256 encrypted uploads, expiry alerts, and instant customer profile linking." },
              { icon: ChatCircleDots, title: "WhatsApp Business Center", desc: "Send bills, updates and receipts from a single dashboard with deep-link CTAs." },
              { icon: Buildings,    title: "Multi-Branch Management",    desc: "Strict branch isolation, RBAC, and a Super-Admin console to move records across branches." },
              { icon: TrendUp,      title: "Analytics Dashboard",        desc: "Real-time revenue, service mix, staff productivity, and government settlement tracking." },
              { icon: ShieldCheck,  title: "Encrypted Cloud Backup",     desc: "One-click .enc backups + restore wizard. Your customer data is bank-grade secure." },
            ].map((f) => {
              const Ic = f.icon;
              return (
                <div key={f.title} className="p-6 rounded-2xl border border-slate-200 hover:border-[#1e3a8a] hover:shadow-md transition-all" data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center mb-3">
                    <Ic size={20} className="text-[#1e3a8a]" weight="duotone" />
                  </div>
                  <div className="font-semibold text-slate-900 mb-1">{f.title}</div>
                  <div className="text-sm text-slate-600 leading-relaxed">{f.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= REVIEWS ================= */}
      <section className="py-20 bg-slate-50" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#1e3a8a] mb-1.5">Customer Reviews</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Trusted by thousands, powered by trust.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { name: "Rekha Patil",   role: "Kolhapur",  stars: 5, text: "Got my PAN card in 2 days. Very smooth process on WhatsApp. Highly recommended!" },
              { name: "Suresh Kumbhar",role: "Sangli",    stars: 5, text: "Rent agreement online + delivery to home. Best CSC in our area. Everything digital, super fast." },
              { name: "Priya Deshpande",role: "Pune",     stars: 5, text: "Aadhaar update + Passport application in one visit. Staff is helpful and prices are clear." },
            ].map((r) => (
              <div key={r.name} className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm" data-testid={`review-${r.name.split(" ")[0].toLowerCase()}`}>
                <div className="flex mb-3">{Array.from({ length: r.stars }).map((_, i) => <Star key={i} size={16} weight="fill" className="text-yellow-500" />)}</div>
                <p className="text-slate-700 text-sm leading-relaxed mb-4">"{r.text}"</p>
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full bg-[#1e3a8a] text-white flex items-center justify-center font-semibold">{r.name[0]}</div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{r.name}</div>
                    <div className="text-[11px] text-slate-500">{r.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CONTACT / BRANCH LOCATOR ================= */}
      <section className="py-20 bg-white" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="rounded-3xl bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6] text-white p-10 md:p-14 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-orange-500/30 rounded-full blur-3xl" />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl sm:text-4xl font-bold mb-3 leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
                  Find your nearest DigitalSetu branch.
                </h3>
                <p className="text-blue-100 mb-6">Walk-in support, WhatsApp help, or apply online — whichever works best for you.</p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/branches" className="inline-flex items-center gap-1.5 px-5 h-11 rounded-full bg-white text-[#1e3a8a] text-sm font-semibold hover:-translate-y-0.5 transition-transform" data-testid="cta-branches">
                    <MapPin size={14} /> Find a Branch
                  </Link>
                  <Link to="/contact" className="inline-flex items-center gap-1.5 px-5 h-11 rounded-full bg-white/10 border border-white/25 text-white text-sm font-semibold hover:bg-white/20" data-testid="cta-contact">
                    <Phone size={14} /> Contact Us
                  </Link>
                </div>
              </div>
              <div className="hidden md:block">
                <div className="grid grid-cols-2 gap-3">
                  {["Reliable", "Same-day", "Cashless", "Encrypted"].map((t) => (
                    <div key={t} className="rounded-xl bg-white/10 border border-white/20 p-4 text-center backdrop-blur">
                      <CheckCircle size={22} weight="fill" className="text-emerald-300 mx-auto mb-1" />
                      <div className="text-sm font-semibold">{t}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Wallet icon isn't exported — provide a shim so we don't need a new import.

function StatCard({ label, value, suffix = "", icon: Ic, tone = "", "data-testid": testid }) {
  return (
    <div className={`rounded-2xl border ${tone} backdrop-blur p-4`} data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-widest text-blue-100/80">{label}</div>
        <Ic size={16} className="text-orange-300" weight="fill" />
      </div>
      <div className="text-2xl font-bold text-white">
        {value.toLocaleString?.() || value}{suffix}
      </div>
    </div>
  );
}
